import React, { useEffect, useMemo, useState } from 'react'
import { Box, Menu, MenuItem, Typography, styled } from '@mui/material'
import { shallow } from 'zustand/shallow'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import SearchIcon from '@mui/icons-material/Search'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Card, ChipToggleGroup, EmptyState, IconButton, SearchInput, Skeleton } from '@/design-system'
import { fireAlertToast } from '@/design-system/alerts'
import useAppStore from '@/store/useAppStore'
import { useLocalStorage } from '@/hooks/common'
import { APP_NAME } from '@/utils/constants'
import CustomTimeAgo from '@/components/CustomTimeAgo'
import { convertToGMT } from '@/utils/helper'
import {
  useConnectionBlock,
  useConnectionDelete,
  useGetConnectRecommendations,
  useGetConnectRequests,
  useGetMyCircles,
  useGetMyConnections,
  useSearchUsers,
} from '@/hooks/api/dashboard'
import { ConnectionRecommendation, ConnectionRequest, MyConnection, SearchConnection } from '@/types'
import { PersonRow } from '../../components/PersonRow'
import { DiscoverCard } from '../../components/DiscoverCard'
import { PersonActionButtons } from '../../components/PersonActionButtons'
import { CirclePickerModal } from '../../components/CirclePickerModal'
import { ConfirmModal } from '../../components/ConfirmModal'
import { useConnectionCircles } from '../../hooks/useConnectionCircles'
import { useConnectionRequestActions } from '../../hooks/useConnectionRequestActions'

const Page = styled(Box)({ maxWidth: 920 })

const PageHead = styled(Box)({ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, marginBottom: 22 })
const H1 = styled(Typography)({ fontSize: 24, fontWeight: 720, letterSpacing: '-0.03em', color: 'var(--ds-ink)' })
const Sub = styled(Typography)({ fontSize: 12.5, color: 'var(--ds-ink-subtle)', marginTop: 3 })

const TabBar = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  borderBottom: '1px solid var(--ds-border)',
  marginBottom: 22,
  overflowX: 'auto',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
})

const TabBtn = styled('button', {
  shouldForwardProp: (p) => p !== '$active',
})<{ $active?: boolean }>(({ $active }) => ({
  all: 'unset',
  boxSizing: 'border-box',
  position: 'relative',
  height: 42,
  padding: '0 4px',
  marginRight: 22,
  fontSize: 13.5,
  fontWeight: 600,
  color: $active ? 'var(--ds-ink)' : 'var(--ds-ink-subtle)',
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: `color var(--ds-duration-fast) var(--ds-ease)`,
  '&:hover': { color: 'var(--ds-ink-muted)' },
  ...($active
    ? {
        '&::after': {
          content: '""',
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: -1,
          height: 2,
          borderRadius: 2,
          background: 'var(--ds-gradient-spectrum)',
        },
      }
    : {}),
}))

const TabCount = styled('span', {
  shouldForwardProp: (p) => p !== '$active' && p !== '$alert',
})<{ $active?: boolean; $alert?: boolean }>(({ $active, $alert }) => ({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 10.5,
  borderRadius: 'var(--ds-radius-pill)',
  padding: '1px 7px',
  color: $alert || $active ? 'var(--ds-inverse-ink)' : 'var(--ds-ink-subtle)',
  background: $alert ? 'var(--ds-accent-deep-a, #e97a4a)' : $active ? 'var(--ds-ink)' : 'var(--ds-hover)',
}))

const ToolRow = styled(Box)({ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18, flexWrap: 'wrap' })

const SearchWrap = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  width: 220,
  maxWidth: '100%',
  height: 34,
  padding: '0 12px',
  borderRadius: 9,
  background: 'var(--ds-hover)',
  marginLeft: 'auto',
})

const ListCard = styled(Card)({
  padding: 0,
  overflow: 'hidden',
  background: 'var(--ds-canvas)',
  boxShadow: 'var(--ds-shadow-soft)',
  '& > *:not(:first-of-type)': { borderTop: '1px solid var(--ds-border)' },
})

const Grid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
  gap: 14,
})

const SkeletonRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '13px 18px',
})

type TabValue = 'connections' | 'requests' | 'sent' | 'discover' | 'search'
const TAB_VALUES: TabValue[] = ['connections', 'requests', 'sent', 'discover', 'search']

function matchesQuery(conn: MyConnection, q: string): boolean {
  if (!q) return true
  const haystack = `${conn.firstName} ${conn.lastName} ${conn.profession} ${conn.location}`.toLowerCase()
  return haystack.includes(q.toLowerCase())
}

function RowListSkeleton({ count = 4 }: { count?: number }) {
  return (
    <ListCard>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonRow key={i}>
          <Skeleton width={46} height={46} rounded='pill' />
          <Box sx={{ flex: 1 }}>
            <Skeleton width={i % 2 === 0 ? 160 : 130} height={13} rounded='sm' sx={{ mb: 1 }} />
            <Skeleton width={200} height={11} rounded='sm' />
          </Box>
        </SkeletonRow>
      ))}
    </ListCard>
  )
}

/**
 * `/connections` — mutual connection graph: your accepted connections,
 * incoming requests, requests you've sent (persisted client-side — see
 * useConnectionRequestActions), and people the backend recommends.
 * Connect/accept both require picking a circle for the person up front (see
 * CirclePickerModal). There's no bulk "list sent requests" endpoint on the
 * backend, so Sent reflects everything we've sent from this browser;
 * cancelling reuses the same connReqUpdate call Decline makes.
 */
export function ConnectionsScreen() {
  const navigate = useNavigate()
  const [basicInfo] = useLocalStorage('ewBasicInfo', null)
  const [searchParams] = useSearchParams()
  // Lets links (e.g. a "wants to connect" notification) deep-link straight into
  // a tab via /connections?tab=requests instead of always landing on the default.
  const initialTab = searchParams.get('tab') as TabValue | null
  const [tab, setTab] = useState<TabValue>(
    initialTab && TAB_VALUES.includes(initialTab) ? initialTab : 'connections',
  )
  const [query, setQuery] = useState('')
  const [circleFilter, setCircleFilter] = useState('all')

  const [myConnections, connRequests, recommendations, myCircles, setConnRequests, searchConnections, removeSentConnectionRequest] =
    useAppStore(
      (state) => [
        state.user.myConnections,
        state.user.connRequests,
        state.user.recommendations,
        state.user.myGroups,
        state.setConnRequests,
        state.user.searchConnections,
        state.removeSentConnectionRequest,
      ],
      shallow,
    )

  const getMyConnections = useGetMyConnections()
  const getConnectRequests = useGetConnectRequests()
  const getRecommendations = useGetConnectRecommendations()
  const getMyCircles = useGetMyCircles()

  const removeConnection = useConnectionDelete()
  const blockConnection = useConnectionBlock()

  // ----- people search -----
  const [rawSearch, setRawSearch] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const searchUsers = useSearchUsers(searchQuery)
  const searchResults: SearchConnection[] = useMemo(() => Object.values(searchConnections ?? {}), [searchConnections])

  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(rawSearch.trim()), 300)
    return () => clearTimeout(t)
  }, [rawSearch])

  useEffect(() => {
    if (!searchQuery) return
    searchUsers.mutate(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery])

  const [initialLoading, setInitialLoading] = useState(true)

  useEffect(() => {
    Promise.allSettled([
      getMyConnections.mutateAsync(1),
      getConnectRequests.mutateAsync(),
      getRecommendations.mutateAsync(),
      getMyCircles.mutateAsync(),
    ]).finally(() => setInitialLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const systemCircles = useMemo(() => myCircles.filter((c) => c.isSystemProvided === '1'), [myCircles])
  const { circlesByUser } = useConnectionCircles(systemCircles)

  // ----- accept / decline / connect / cancel (shared with the global search People tab) -----
  const {
    circlePicker,
    openAccept,
    openConnect,
    closeCirclePicker,
    onSubmitCircle,
    onDecline,
    onCancelSent,
    decliningID,
    cancellingID,
    sentIDs,
    sentConnectionRequests,
    connecting,
    accepting,
  } = useConnectionRequestActions({
    onAccepted: (connReqID) => {
      setConnRequests(connRequests.filter((r) => r.connReqID !== connReqID))
      getConnectRequests.mutate()
      getMyConnections.mutate(1)
    },
    onDeclined: (connReqID) => {
      setConnRequests(connRequests.filter((r) => r.connReqID !== connReqID))
      getConnectRequests.mutate()
    },
  })

  // A sent request resolves (accepted elsewhere) without us hearing about
  // it directly — drop it from the persisted list once the person shows up
  // in myConnections so Sent doesn't accumulate stale rows.
  useEffect(() => {
    sentConnectionRequests.forEach((r) => {
      if (myConnections.some((c) => c.userID === r.userID)) removeSentConnectionRequest(r.userID)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myConnections])

  const sentTo = sentIDs

  // ----- remove / block / view profile (via row menu) -----
  const [rowMenu, setRowMenu] = useState<{ anchor: HTMLElement; userID: string; name: string } | null>(null)
  const [confirmAction, setConfirmAction] = useState<{ type: 'remove' | 'block'; userID: string; name: string } | null>(null)
  const [confirming, setConfirming] = useState(false)

  const onConfirmAction = async () => {
    if (!confirmAction) return
    setConfirming(true)
    try {
      if (confirmAction.type === 'remove') {
        await removeConnection.mutateAsync(confirmAction.userID)
        fireAlertToast('connection.removed')
      } else {
        await blockConnection.mutateAsync(confirmAction.userID)
        fireAlertToast('connection.blocked')
      }
      setConfirmAction(null)
    } catch {
      fireAlertToast(confirmAction.type === 'remove' ? 'connection.removeFailed' : 'connection.blockFailed')
    } finally {
      setConfirming(false)
    }
  }

  const filteredConnections = useMemo(() => {
    return myConnections
      .filter((c) => matchesQuery(c, query))
      .filter((c) => circleFilter === 'all' || (circlesByUser[c.userID] ?? []).includes(circleFilter))
  }, [myConnections, query, circleFilter, circlesByUser])

  const connCount = basicInfo?.connCount ?? myConnections.length

  return (
    <Page>
      <PageHead>
        <Box>
          <H1>Connections</H1>
          <Sub>{connCount} people you&apos;ve connected with across {APP_NAME}.</Sub>
        </Box>
      </PageHead>

      <TabBar>
        <TabBtn $active={tab === 'connections'} onClick={() => setTab('connections')}>
          Connections
          {!initialLoading && <TabCount $active={tab === 'connections'}>{myConnections.length}</TabCount>}
        </TabBtn>
        <TabBtn $active={tab === 'requests'} onClick={() => setTab('requests')}>
          Requests
          {!initialLoading && connRequests.length > 0 && <TabCount $alert>{connRequests.length}</TabCount>}
        </TabBtn>
        <TabBtn $active={tab === 'sent'} onClick={() => setTab('sent')}>
          Sent
          {sentConnectionRequests.length > 0 && <TabCount $active={tab === 'sent'}>{sentConnectionRequests.length}</TabCount>}
        </TabBtn>
        <TabBtn $active={tab === 'discover'} onClick={() => setTab('discover')}>
          Discover
        </TabBtn>
        <TabBtn $active={tab === 'search'} onClick={() => setTab('search')} sx={{ marginLeft: 'auto', marginRight: 0 }}>
          <SearchIcon sx={{ fontSize: 15 }} />
          Search people
        </TabBtn>
      </TabBar>

      {tab === 'connections' && (
        <>
          <ToolRow>
            <ChipToggleGroup
              options={[{ value: 'all', label: 'All circles' }, ...systemCircles.map((c) => ({ value: c.circleName, label: c.circleName }))]}
              value={circleFilter}
              onChange={setCircleFilter}
            />
            <SearchWrap>
              <SearchInput
                icon='⌕'
                placeholder='Filter connections…'
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                sx={{ fontSize: 12.5 }}
              />
            </SearchWrap>
          </ToolRow>

          {initialLoading ? (
            <RowListSkeleton />
          ) : filteredConnections.length === 0 ? (
            <EmptyState
              icon={<span style={{ fontSize: 32, opacity: 0.35 }}>◎</span>}
              title={myConnections.length === 0 ? 'No connections yet' : 'No matches'}
              description={
                myConnections.length === 0
                  ? 'Accept a request or send one from Discover to start building your circle.'
                  : 'Try a different name, circle, or location.'
              }
            />
          ) : (
            <ListCard>
              {filteredConnections.map((conn) => (
                <PersonRow
                  key={conn.userID}
                  avatarUrl={conn.thumbImgURL}
                  name={`${conn.firstName} ${conn.lastName}`}
                  profession={conn.profession}
                  location={conn.location}
                  circleLabel={circlesByUser[conn.userID]?.[0]}
                  onClick={() => navigate(`/profile/${conn.userID}`)}
                  actions={
                    <IconButton
                      tone='quiet'
                      aria-label={`Options for ${conn.firstName}`}
                      onClick={(e) =>
                        setRowMenu({ anchor: e.currentTarget, userID: conn.userID, name: `${conn.firstName} ${conn.lastName}` })
                      }
                    >
                      <MoreHorizIcon />
                    </IconButton>
                  }
                />
              ))}
            </ListCard>
          )}
        </>
      )}

      {tab === 'requests' &&
        (initialLoading ? (
          <RowListSkeleton count={2} />
        ) : connRequests.length === 0 ? (
          <EmptyState
            icon={<span style={{ fontSize: 32, opacity: 0.35 }}>◎</span>}
            title='No pending requests'
            description="You're all caught up — new connection requests will show up here."
          />
        ) : (
          <ListCard>
            {connRequests.map((req: ConnectionRequest) => (
              <PersonRow
                key={req.connReqID}
                avatarUrl={req.profileAlbumPic || req.profilePic}
                name={req.requesterName || `${req.rFname} ${req.rLname}`}
                subLine={
                  req.reqReceivedTime ? (
                    <>
                      Wants to connect · <CustomTimeAgo date={convertToGMT(req.reqReceivedTime)} /> ago
                    </>
                  ) : undefined
                }
                actions={
                  <PersonActionButtons
                    state='received'
                    busy={decliningID === req.connReqID}
                    onDecline={() => onDecline(req.connReqID)}
                    onAccept={() => openAccept(req.connReqID, req.requesterName || req.rFname)}
                  />
                }
              />
            ))}
          </ListCard>
        ))}

      {tab === 'sent' &&
        (sentConnectionRequests.length === 0 ? (
          <EmptyState
            icon={<span style={{ fontSize: 32, opacity: 0.35 }}>◎</span>}
            title='No sent requests'
            description='Requests you send from Discover or Search will show up here until they’re accepted.'
          />
        ) : (
          <ListCard>
            {sentConnectionRequests.map((s) => (
              <PersonRow
                key={s.userID}
                avatarUrl={s.avatarUrl}
                name={s.name}
                profession={s.profession}
                location={s.location}
                subLine={
                  <>
                    Sent <CustomTimeAgo date={convertToGMT(s.sentAt)} /> ago
                  </>
                }
                actions={
                  <PersonActionButtons
                    state='sent'
                    cancelling={cancellingID === s.userID}
                    onCancelSent={() => onCancelSent(s.userID)}
                  />
                }
              />
            ))}
          </ListCard>
        ))}

      {tab === 'discover' &&
        (initialLoading ? (
          <Grid>
            {Array.from({ length: 4 }).map((_, i) => (
              <Box key={i} sx={{ background: 'var(--ds-canvas)', borderRadius: 'var(--ds-radius-lg)', boxShadow: 'var(--ds-shadow-soft)', p: '22px 18px 18px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Skeleton width={60} height={60} rounded='pill' sx={{ mb: 1.5 }} />
                <Skeleton width={100} height={13} rounded='sm' sx={{ mb: 1 }} />
                <Skeleton width={130} height={11} rounded='sm' />
              </Box>
            ))}
          </Grid>
        ) : recommendations.length === 0 ? (
          <EmptyState
            icon={<span style={{ fontSize: 32, opacity: 0.35 }}>✦</span>}
            title='Nothing to discover right now'
            description='Recommendations are based on your circles and mutual activity — check back soon.'
          />
        ) : (
          <Grid>
            {recommendations.map((rec: ConnectionRecommendation) => (
              <DiscoverCard
                key={rec.userID}
                avatarUrl={rec.profilePicURL}
                name={`${rec.firstName} ${rec.lastName}`}
                profession={rec.profession}
                location={rec.location}
                reason={rec.motto || `${rec.currentCount} connections`}
                connectState={
                  sentTo.has(rec.userID) ? 'sent' : circlePicker?.mode === 'connect' && circlePicker.userID === rec.userID && connecting ? 'sending' : 'idle'
                }
                onConnect={() =>
                  openConnect({
                    userID: rec.userID,
                    name: `${rec.firstName} ${rec.lastName}`,
                    profession: rec.profession,
                    location: rec.location,
                    avatarUrl: rec.profilePicURL,
                  })
                }
                onClick={() => navigate(`/profile/${rec.userID}`)}
              />
            ))}
          </Grid>
        ))}

      {tab === 'search' && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              height: 44,
              padding: '0 14px',
              borderRadius: 'var(--ds-radius-md)',
              background: 'var(--ds-canvas)',
              boxShadow: 'var(--ds-shadow-soft)',
              border: '1px solid var(--ds-border)',
              mb: 3,
              '&:focus-within': { borderColor: 'var(--ds-ink-muted)' },
            }}
          >
            <SearchIcon sx={{ fontSize: 18, color: 'var(--ds-ink-subtle)', flexShrink: 0 }} />
            <SearchInput
              icon=''
              placeholder='Search for people by name…'
              value={rawSearch}
              onChange={(e) => setRawSearch(e.target.value)}
              sx={{ fontSize: 14, flex: 1 }}
              autoFocus
            />
          </Box>

          {!searchQuery ? (
            <EmptyState
              icon={<SearchIcon sx={{ fontSize: 34, opacity: 0.25 }} />}
              title={`Find people on ${APP_NAME}`}
              description='Search by name to discover and connect with people you know.'
            />
          ) : searchUsers.isLoading ? (
            <RowListSkeleton count={5} />
          ) : searchResults.length === 0 ? (
            <EmptyState
              icon={<span style={{ fontSize: 32, opacity: 0.35 }}>◎</span>}
              title={`No results for "${searchQuery}"`}
              description='Try a different name or spelling.'
            />
          ) : (
            <ListCard>
              {searchResults.map((result) => {
                const isConnected = result.isConnection
                const isSent = sentTo.has(result.userID) || result.reqSent
                const isReceived = Boolean(result.reqReceived)
                const pendingReqID = connRequests.find((r) => r.requestorID === result.userID)?.connReqID

                return (
                  <PersonRow
                    key={result.userID}
                    avatarUrl={result.profilePicThumbURL || result.profilePicURL}
                    name={`${result.firstName} ${result.lastName}`}
                    profession={result.profession}
                    location={result.location}
                    onClick={() => navigate(`/profile/${result.userID}`)}
                    actions={
                      <PersonActionButtons
                        state={isConnected ? 'connected' : isSent ? 'sent' : isReceived && pendingReqID ? 'received' : 'none'}
                        busy={decliningID === pendingReqID}
                        cancelling={cancellingID === result.userID}
                        onDecline={() => pendingReqID && onDecline(pendingReqID)}
                        onAccept={() => pendingReqID && openAccept(pendingReqID, `${result.firstName} ${result.lastName}`)}
                        onConnect={() =>
                          openConnect({
                            userID: result.userID,
                            name: `${result.firstName} ${result.lastName}`,
                            profession: result.profession,
                            location: result.location,
                            avatarUrl: result.profilePicThumbURL || result.profilePicURL,
                          })
                        }
                        onCancelSent={() => onCancelSent(result.userID)}
                      />
                    }
                  />
                )
              })}
            </ListCard>
          )}
        </>
      )}

      <Menu
        anchorEl={rowMenu?.anchor ?? null}
        open={Boolean(rowMenu)}
        onClose={() => setRowMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: { mt: 1, minWidth: 170, borderRadius: 'var(--ds-radius-md)', boxShadow: 'var(--ds-shadow-soft)', border: '1px solid var(--ds-border)' },
        }}
      >
        <MenuItem
          sx={{ fontSize: 14 }}
          onClick={() => {
            if (rowMenu) navigate(`/profile/${rowMenu.userID}`)
            setRowMenu(null)
          }}
        >
          View profile
        </MenuItem>
        <MenuItem
          sx={{ fontSize: 14 }}
          onClick={() => {
            if (rowMenu) setConfirmAction({ type: 'remove', userID: rowMenu.userID, name: rowMenu.name })
            setRowMenu(null)
          }}
        >
          Remove connection
        </MenuItem>
        <MenuItem
          sx={{ fontSize: 14, color: 'var(--ds-danger)' }}
          onClick={() => {
            if (rowMenu) setConfirmAction({ type: 'block', userID: rowMenu.userID, name: rowMenu.name })
            setRowMenu(null)
          }}
        >
          Block
        </MenuItem>
      </Menu>

      <CirclePickerModal
        open={Boolean(circlePicker)}
        onClose={closeCirclePicker}
        personName={circlePicker?.name ?? ''}
        submitLabel={circlePicker?.mode === 'accept' ? 'Accept' : 'Send request'}
        circles={systemCircles}
        loading={accepting || connecting}
        onSubmit={onSubmitCircle}
      />

      <ConfirmModal
        open={Boolean(confirmAction)}
        onClose={() => setConfirmAction(null)}
        title={confirmAction?.type === 'remove' ? 'Remove this connection?' : 'Block this person?'}
        description={
          confirmAction?.type === 'remove'
            ? `${confirmAction.name} will be removed from your connections. You can reconnect anytime.`
            : `${confirmAction?.name} won't be able to see your profile or reach you. You can't undo this from here.`
        }
        confirmLabel={confirmAction?.type === 'remove' ? 'Remove' : 'Block'}
        loading={confirming}
        onConfirm={onConfirmAction}
      />
    </Page>
  )
}
